import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  CalendarCheck, MapPin, Loader2, AlertCircle, Calendar, Tag, HelpCircle,
  Search, ArrowRight, CheckCircle2, Video, X,
} from "lucide-react";
import { useGetPublicEventsQuery } from "../api/eventsApi";

/* ========================================================================== */
/* Formatting                                                                 */
/* ========================================================================== */

const naira = (n: number) => `₦${Number(n || 0).toLocaleString()}`;

function fmt(d: string) {
  return new Date(d).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" });
}

/**
 * The date as a card corner reads it: a big day over a small month. Two dates
 * in one line is what an event spanning days actually is, so the range is
 * printed underneath rather than crammed in here.
 */
function dayBox(d: string) {
  const date = new Date(d);
  return {
    day:   date.toLocaleDateString("en-NG", { day: "2-digit" }),
    month: date.toLocaleDateString("en-NG", { month: "short" }).toUpperCase(),
  };
}

/** "Oct 14 – Oct 16, 2026", collapsed to one date for a single-day event. */
function dateRange(start: string, end: string) {
  if (!start) return "";
  if (!end || end === start) return fmt(start);
  return `${new Date(start).toLocaleDateString("en-NG", { day: "numeric", month: "short" })} – ${fmt(end)}`;
}

/** The first plain-text line of an event's own description. */
function excerpt(event: any): string {
  const text = event.tagline || event.details || event.theme || "";
  return String(text).replace(/\s+/g, " ").trim();
}

/* ========================================================================== */
/* Pricing                                                                    */
/* ========================================================================== */

/**
 * What this viewer pays. The API resolves `your_price` from their membership
 * grade; when it can't (guest, or no tier for their grade) the card shows the
 * cheapest tier as "from X" rather than a price we can't promise them.
 */
function priceLabel(event: any): string | null {
  // An event that charges nothing has no fee to advertise. Null, so the card
  // leaves the block out rather than printing the word "Free" at it.
  if (!event.is_paid) return null;

  if (event.price_is_resolved !== false && event.your_price != null) {
    return naira(event.your_price);
  }
  if (event.price_from != null) return `From ${naira(event.price_from)}`;

  // Pre-resolution fallback: cheapest tier we can see, else the flat price.
  // `current_amount`, not `amount` — a card must not advertise an early-bird
  // fee once the early-bird window has closed.
  const tiers: any[] = event.category_prices ?? [];
  if (tiers.length) {
    return `From ${naira(Math.min(...tiers.map((t) => Number(t.current_amount ?? t.amount ?? 0))))}`;
  }
  return naira(event.price);
}

/** Whose price that is — their grade, or nobody's in particular. */
function priceCaption(event: any): string | null {
  if (!event.is_paid) return null;
  if (event.price_is_resolved !== false && event.your_category_label) {
    return event.your_category_label;
  }
  const tiers: any[] = event.category_prices ?? [];
  return tiers.length > 1 ? `${tiers.length} categories` : null;
}

/* ========================================================================== */
/* Filters                                                                    */
/* ========================================================================== */

type DateRange = "upcoming" | "month" | "past" | "all";

const DATE_RANGES: { value: DateRange; label: string }[] = [
  { value: "upcoming", label: "All Upcoming" },
  { value: "month",    label: "This Month"   },
  { value: "past",     label: "Past Events"  },
  { value: "all",      label: "All Dates"    },
];

const HOSTS: { value: string; label: string }[] = [
  { value: "",          label: "All Types"          },
  { value: "niqs",      label: "NIQS National"      },
  { value: "chapter",   label: "Chapter"            },
  { value: "institute", label: "Accredited Institute" },
];

const STATUSES: { value: string; label: string }[] = [
  { value: "",          label: "All Statuses" },
  { value: "published", label: "Open"         },
  { value: "completed", label: "Completed"    },
  { value: "cancelled", label: "Cancelled"    },
];

const STATUS_PILLS: Record<string, { label: string; cls: string }> = {
  published: { label: "Open",      cls: "bg-emerald-600 text-white" },
  completed: { label: "Completed", cls: "bg-gray-700 text-white"    },
  cancelled: { label: "Cancelled", cls: "bg-rose-600 text-white"    },
  draft:     { label: "Draft",     cls: "bg-amber-500 text-white"   },
};

/** What an event is, in the words the card has room for. */
function hostLabel(event: any): string {
  if (event.hosted_by === "chapter")   return `${event.host_chapter?.name ?? "Chapter"} Chapter`;
  if (event.hosted_by === "institute") return event.host_institute?.acronym ?? event.host_institute?.name ?? "Institute";
  return "NIQS National";
}

export default function EventsListingPage() {
  const { data: events = [], isLoading, isError } = useGetPublicEventsQuery();

  const [search, setSearch]   = useState("");
  const [range, setRange]     = useState<DateRange>("upcoming");
  const [host, setHost]       = useState("");
  const [status, setStatus]   = useState("");

  const filtered = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const monthEnd   = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59);

    return (events as any[]).filter((e) => {
      // An event is "upcoming" until the day it ENDS has passed — a two-day
      // conference is still on on its second morning.
      const ends   = e.end_date ? new Date(e.end_date) : null;
      const starts = e.start_date ? new Date(e.start_date) : null;

      if (range === "upcoming" && ends && ends < today) return false;
      if (range === "past"     && ends && ends >= today) return false;
      if (range === "month" && starts && (starts < monthStart || starts > monthEnd)) return false;

      if (host && e.hosted_by !== host) return false;
      if (status && e.status !== status) return false;

      if (search) {
        const q = search.toLowerCase();
        const hay = [e.title, e.location, e.theme, e.tagline].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }

      return true;
    });
  }, [events, range, host, status, search]);

  // The filters actually narrowing the list, so they can be read back and
  // dropped one at a time rather than only cleared wholesale.
  const chips = [
    range !== "all"
      ? { key: "range",  label: `Date: ${DATE_RANGES.find((r) => r.value === range)?.label}`, clear: () => setRange("all") }
      : null,
    host   ? { key: "host",   label: HOSTS.find((h) => h.value === host)?.label ?? host,     clear: () => setHost("")     } : null,
    status ? { key: "status", label: STATUSES.find((s) => s.value === status)?.label ?? status, clear: () => setStatus("") } : null,
    search ? { key: "search", label: `"${search}"`, clear: () => setSearch("") } : null,
  ].filter(Boolean) as { key: string; label: string; clear: () => void }[];

  const selectCls =
    "w-full appearance-none bg-white border border-gray-200 rounded-2xl pl-10 pr-9 py-3 text-sm font-semibold text-gray-800 outline-none focus:border-indigo-400 focus:ring-4 focus:ring-indigo-50 transition-all cursor-pointer";

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 pb-20 space-y-6">

      {/* ── Heading ─────────────────────────────────────────────────────── */}
      <div>
        <h1 className="text-3xl font-black text-gray-900 tracking-tight">Events</h1>
        <p className="text-gray-500 text-sm mt-1 font-medium">
          Conferences, workshops and CPD sessions across the Institute. Fees are shown for your grade.
        </p>
      </div>

      {/* ── Filter bar ──────────────────────────────────────────────────── */}
      <div className="bg-white border border-gray-100 rounded-3xl p-5 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Field label="Date Range" icon={<Calendar className="w-4 h-4" />}>
            <select className={selectCls} value={range} onChange={(e) => setRange(e.target.value as DateRange)}>
              {DATE_RANGES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </Field>

          <Field label="Event Type" icon={<Tag className="w-4 h-4" />}>
            <select className={selectCls} value={host} onChange={(e) => setHost(e.target.value)}>
              {HOSTS.map((h) => <option key={h.value} value={h.value}>{h.label}</option>)}
            </select>
          </Field>

          <Field label="Status" icon={<HelpCircle className="w-4 h-4" />}>
            <select className={selectCls} value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </Field>

          <Field label="Search" icon={<Search className="w-4 h-4" />}>
            <input
              className={selectCls.replace("appearance-none ", "").replace(" cursor-pointer", "")}
              placeholder="Title or location…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Field>
        </div>
      </div>

      {/* ── Result count + active filters ───────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-gray-500">
          Showing <strong className="text-gray-900 font-black">{filtered.length}</strong>{" "}
          {filtered.length === 1 ? "event" : "events"}
        </span>
        {chips.map((c) => (
          <button
            key={c.key}
            onClick={c.clear}
            className="inline-flex items-center gap-1.5 bg-white border border-gray-200 rounded-full pl-3 pr-2 py-1 text-xs font-bold text-gray-600 hover:border-gray-300 hover:text-gray-900 transition-colors"
          >
            {c.label}
            <X className="w-3 h-3" />
          </button>
        ))}
      </div>

      {/* ── Cards ───────────────────────────────────────────────────────── */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
        </div>
      ) : isError ? (
        <div className="bg-rose-50 border border-rose-100 rounded-3xl p-8 flex items-center gap-4">
          <AlertCircle className="w-6 h-6 text-rose-500 shrink-0" />
          <p className="text-rose-700 font-semibold">Failed to load events. Please try again.</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-gray-100 rounded-3xl p-16 text-center">
          <CalendarCheck className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-bold">No events match these filters.</p>
          {chips.length > 0 && (
            <button
              onClick={() => { setRange("all"); setHost(""); setStatus(""); setSearch(""); }}
              className="mt-4 text-sm font-black text-indigo-600 hover:underline"
            >
              Clear all filters
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {filtered.map((event: any) => <EventCard key={event.id} event={event} />)}
        </div>
      )}
    </div>
  );
}

/* ========================================================================== */
/* Pieces                                                                     */
/* ========================================================================== */

function Field({ label, icon, children }: { label: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[11px] font-bold text-gray-500 mb-1.5">{label}</label>
      <div className="relative">
        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">{icon}</span>
        {children}
      </div>
    </div>
  );
}

function EventCard({ event }: { event: any }) {
  const pill  = STATUS_PILLS[event.status] ?? { label: event.status, cls: "bg-gray-600 text-white" };
  const box   = dayBox(event.start_date);
  const blurb = excerpt(event);

  // Virtual is a fee the event sells, so a card can say it is available online
  // without anybody having to open it.
  const hasVirtual = (event.category_prices ?? []).some((t: any) => t.attendance_mode === "virtual");
  const fee   = priceLabel(event);

  return (
    <Link
      to={`/events/${event.id}`}
      className="group flex flex-col bg-white border border-gray-100 rounded-3xl overflow-hidden shadow-sm hover:shadow-lg hover:border-gray-200 transition-all duration-200"
    >
      {/* Banner */}
      <div className="relative h-48 shrink-0 overflow-hidden">
        {event.background_image ? (
          <img
            src={event.background_image}
            alt=""
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-gray-900 via-indigo-900 to-indigo-700 flex items-center justify-center">
            <CalendarCheck className="w-14 h-14 text-white/20" />
          </div>
        )}

        <div className="absolute inset-x-0 top-0 p-3 flex items-start justify-between gap-2">
          <span className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full shadow-sm ${pill.cls}`}>
            <CheckCircle2 className="w-3 h-3" /> {pill.label}
          </span>
          <span className="text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full bg-white/95 text-gray-700 shadow-sm">
            {hostLabel(event)}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-col flex-1 p-6">
        <div className="flex items-start justify-between gap-4">
          <h2 className="font-black text-gray-900 text-lg leading-snug line-clamp-2 group-hover:text-indigo-700 transition-colors">
            {event.title}
          </h2>
          <div className="shrink-0 text-center bg-gray-50 border border-gray-100 rounded-2xl px-3 py-2 leading-none">
            <div className="text-xl font-black text-gray-900">{box.day}</div>
            <div className="text-[10px] font-black text-gray-400 tracking-widest mt-0.5">{box.month}</div>
          </div>
        </div>

        <div className="flex items-center gap-2 text-sm text-gray-500 font-medium mt-3">
          <CalendarCheck className="w-4 h-4 text-gray-400 shrink-0" />
          {dateRange(event.start_date, event.end_date)}
        </div>

        {blurb && <p className="text-sm text-gray-500 font-medium leading-relaxed mt-3 line-clamp-3">{blurb}</p>}

        {/* Badges that change what the event IS, not how it looks */}
        <div className="flex flex-wrap gap-1.5 mt-3">
          {event.is_cpd && (
            <span className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-lg bg-violet-50 text-violet-700 border border-violet-100">
              +{event.cpd_points} CPD
            </span>
          )}
          {hasVirtual && (
            <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-lg bg-sky-50 text-sky-700 border border-sky-100">
              <Video className="w-3 h-3" /> Virtual option
            </span>
          )}
          {event.is_members_only && (
            <span className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-lg bg-indigo-50 text-indigo-700 border border-indigo-100">
              Members only
            </span>
          )}
          {event.is_registered && (
            <span className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-lg border ${
              event.my_registration?.payment_status === "pending"
                ? "bg-amber-50 text-amber-700 border-amber-100"
                : "bg-emerald-50 text-emerald-700 border-emerald-100"
            }`}>
              <CheckCircle2 className="w-3 h-3" />
              {event.my_registration?.payment_status === "pending" ? "Fee due" : "Registered"}
            </span>
          )}
        </div>

        {/* The fee, given its own line — it is the thing people came to read.
            An event that charges nothing leaves the block out altogether. */}
        <div className={`mt-5 pt-4 border-t border-gray-100 flex items-end gap-3 ${fee ? "justify-between" : "justify-end"}`}>
          {fee && (
            <div className="min-w-0">
              <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5">
                Fee
              </div>
              <div className="text-lg font-black leading-none text-gray-900">{fee}</div>
              <div className="text-[11px] font-bold text-gray-400 mt-1 truncate">
                {priceCaption(event) ?? ""}
                {event.has_pricing_timeline && event.price_window_label
                  ? `${priceCaption(event) ? " · " : ""}${event.price_window_label} rate`
                  : ""}
              </div>
            </div>
          )}
          <span className="inline-flex items-center gap-1 text-sm font-black text-indigo-600 group-hover:gap-2 transition-all shrink-0">
            {event.is_registered ? "View" : "Register"} <ArrowRight className="w-4 h-4" />
          </span>
        </div>

        {event.location && (
          <div className="flex items-center gap-2 text-xs text-gray-400 font-semibold mt-3">
            <MapPin className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">{event.location}</span>
          </div>
        )}
      </div>
    </Link>
  );
}
